import type { BlockType } from '@unifyapps/defs/types/block';
import { UAResources } from '@unifyapps/defs/types/resources';
import { EMPTY_ARRAY } from '../../../../../consts/empty';
import { getSelectedDataSourcePath } from '../../../../../utils/getSelectedDataSourcePath';
import type { DataSourceRecordStoreState } from '../../../../stores/DataSourceRecordStore';
import {
  getBlockContentDataSource,
  getDataSourceHasAggregationMetadata,
} from '../../../../utils/dataSourceMetadata';

export const COMPONENT_TYPES_WITH_AGGREGATION_METADATA = new Set<string>([
  'Filters',
  'CategoryFilter',
  'Table',
  'NestedTable',
  'GroupTable',
]);

export function getDataSourcesForAggregationMetadata(
  block: BlockType,
  dataSources: DataSourceRecordStoreState['dataSources'],
) {
  if (!COMPONENT_TYPES_WITH_AGGREGATION_METADATA.has(block.component.componentType)) {
    return EMPTY_ARRAY as string[];
  }

  if (
    block.component.componentType === 'Filters' ||
    block.component.componentType === 'CategoryFilter'
  ) {
    return block.component.content.dataSourceIds;
  } else if (
    block.component.componentType === 'Table' ||
    block.component.componentType === 'GroupTable' ||
    block.component.componentType === 'NestedTable'
  ) {
    const topToolbar = block.component.content.addOns?.topToolbar;
    // a string source is always Mapped (Manual persists an array)
    const mappedFilterSource =
      typeof topToolbar?.aggregationMetadataSource === 'string' &&
      topToolbar.aggregationMetadataSource.trim().length > 0
        ? topToolbar.aggregationMetadataSource
        : undefined;
    const { dataSource, dataSourceId } = getBlockContentDataSource(block, dataSources);
    const hasAggregationMetadata = getDataSourceHasAggregationMetadata({
      dataSource,
      dataSourceId,
    });

    // when the user has mapped filter source in filter addon, we only want to fetch metadata for that source
    if (mappedFilterSource) {
      const { dataSourceId: filterSourceDataSourceId } = getSelectedDataSourcePath({
        data: mappedFilterSource,
      });
      // this dataSourceId could be one of AGGREGATE_METADATA_DATA_SOURCE_RESOURCE_NAME
      return filterSourceDataSourceId ? [filterSourceDataSourceId] : [];
      // if the main data source has aggregation metadata, we want to fetch metadata for that source
    } else if (hasAggregationMetadata) {
      return dataSourceId ? [dataSourceId] : [];
    }

    return EMPTY_ARRAY as string[];
  }
}

// these kind of resourcename would be used in Filter block or as a filter source in Table block
export const AGGREGATE_METADATA_DATA_SOURCE_RESOURCE_NAME = new Set<string>([
  UAResources.AnalyticsByUAAggregateMetadata,
  UAResources.StorageByUAAggregateMetadata,
  UAResources.CallablesCallAutomation,
]);

// because we don't have access to blocks when triggering aggregateMetadata from global page,
// so that is why we don't include the callables in this array, keeping them in this array was resulting them
// being fired on unmount though the callable might not be related to fetching aggregate metadata
export const GLOBAL_PAGE_AGGREGATE_METADATA_DATA_SOURCE_RESOURCE_NAME = new Set<string>([
  UAResources.AnalyticsByUAAggregateMetadata,
  UAResources.StorageByUAAggregateMetadata,
]);
